<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import getWootAxios from 'dashboard/helper/getWootAxios';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { accountId } = useAccount();
const showAlert = useAlert;

const isLoading = ref(false);
const pack = ref({
  enabled: false,
  count: 0,
  last_updated_at: null,
  items: [],
  config: {},
});

const dialogRef = ref(null);
const isRebuilding = ref(false);

const label = computed(() => {
  if (!pack.value.enabled) return 'Account memory: OFF';
  const n = Number(pack.value.count || 0);
  return `Account memory: ON (${n})`;
});

const subLabel = computed(() => {
  if (!pack.value.enabled) return 'Disabled in Configure Settings - AI';
  return pack.value.last_updated_at
    ? `Updated: ${pack.value.last_updated_at}`
    : 'No items yet';
});

const fetchPack = async () => {
  isLoading.value = true;
  try {
    const resp = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/account_memory_pack`
    );
    pack.value = resp?.data || pack.value;
  } catch (e) {
    // silent in header
  } finally {
    isLoading.value = false;
  }
};

const rebuild = async () => {
  isRebuilding.value = true;
  try {
    await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/account_memory_pack/rebuild`,
      { conversations_limit: 50 }
    );
    showAlert('Rebuild started. New memory items will appear shortly.');
  } catch (e) {
    showAlert(
      e?.response?.data?.error ||
        e?.message ||
        'Failed to start rebuild (admin only)'
    );
  } finally {
    isRebuilding.value = false;
  }
};

const open = async () => {
  await fetchPack();
  dialogRef.value?.open();
};

onMounted(fetchPack);
</script>

<template>
  <div class="account-memory-pill">
    <button
      type="button"
      class="account-memory-pill__btn"
      :class="{ 'account-memory-pill__btn--off': !pack.enabled }"
      @click="open"
    >
      <span class="account-memory-pill__title">{{ label }}</span>
      <span class="account-memory-pill__sub">{{ subLabel }}</span>
    </button>
  </div>

  <Dialog
    ref="dialogRef"
    title="Account Memory Pack"
    description="Fixed-size account memory items distilled from resolved conversations. Used to keep replies consistent without growing prompt history."
    width="3xl"
    :show-confirm-button="false"
  >
    <div class="flex items-center justify-between gap-3">
      <div class="text-sm text-n-slate-11">
        <span v-if="pack.enabled">
          Items: <b>{{ pack.count }}</b>
          <span v-if="pack.config?.max_items"> / max {{ pack.config.max_items }}</span>
        </span>
        <span v-else>Disabled</span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="pack.enabled"
          color="ruby"
          variant="outline"
          size="sm"
          label="Rebuild"
          :is-loading="isRebuilding"
          :disabled="isRebuilding || isLoading"
          @click="rebuild"
        />
        <Button
          color="slate"
          variant="outline"
          size="sm"
          label="Refresh"
          :is-loading="isLoading"
          :disabled="isLoading || isRebuilding"
          @click="fetchPack"
        />
      </div>
    </div>

    <div v-if="!pack.enabled" class="mt-4 text-sm text-n-slate-11">
      Enable Account Memory Pack in <b>Superadmin → Configure Settings - AI</b>.
    </div>

    <div v-else class="mt-4 overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-2 pr-4">Type</th>
            <th class="py-2 pr-4">Title</th>
            <th class="py-2 pr-4">Content</th>
            <th class="py-2 pr-0">Updated</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="it in pack.items || []"
            :key="it.id"
            class="border-t border-n-alpha-2 align-top"
          >
            <td class="py-2 pr-4 text-n-slate-11">{{ it.item_type }}</td>
            <td class="py-2 pr-4 text-n-slate-12">{{ it.title || '-' }}</td>
            <td class="py-2 pr-4 text-n-slate-11">{{ it.content }}</td>
            <td class="py-2 pr-0 text-n-slate-11">
              {{ it.updated_at || '-' }}
            </td>
          </tr>
          <tr v-if="!(pack.items || []).length">
            <td colspan="4" class="py-3 text-n-slate-11">No items yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </Dialog>
</template>

<style scoped>
.account-memory-pill__btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: 3px 8px;
  border-radius: 8px;
  border: 1px solid var(--wlab-border-default);
  background: var(--wlab-bg-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.account-memory-pill__btn:hover {
  border-color: var(--wlab-accent);
  background: var(--wlab-bg-hover);
}

.account-memory-pill__btn--off {
  opacity: 0.8;
}

.account-memory-pill__title {
  font-size: 10px;
  line-height: 12px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  white-space: nowrap;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-memory-pill__sub {
  font-size: 9px;
  line-height: 11px;
  color: var(--wlab-text-muted);
  white-space: nowrap;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
